import { Button } from "@/components/ui/button";
import { Metadata } from "@/types";
import { PlusIcon } from "@heroicons/react/outline";
import { CheckIcon } from "lucide-react";
import EditableContent from "./EditableContent";
import useMetadataManager from "./hooks/useMetadataManager";
import ReadOnlyContent from "./ReadOnlyContent";

interface MetadataSectionProps {
  metadata: Metadata;
  setMetadata?: (metadata: Metadata) => void;
  readonly?: boolean;
}

const MetadataSection = ({
  metadata,
  setMetadata,
  readonly = false,
}: MetadataSectionProps) => {
  const metadataManager = useMetadataManager(metadata, setMetadata);
  const {
    metadataArray,
    jsonToggleByIndex,
    error,
    saveSuccess,
    handleInputChange,
    addNewRow,
    deleteRow,
    toggleJsonEditor,
    validateAndSave,
  } = metadataManager;

  return (
    <div className="flex h-full w-full flex-col">
      {!readonly && error && (
        <div className="text-xs text-red-500">{error}</div>
      )}
      {!readonly && saveSuccess && (
        <div className="flex items-center text-xs text-green-600">
          <CheckIcon className="mr-1 h-3 w-3" />
          Metadata updated successfully
        </div>
      )}

      <div
        className={`flex-grow overflow-y-auto rounded-md border border-gray-200 p-2 ${
          readonly ? "max-h-[150px]" : "max-h-[200px]"
        }`}
      >
        {readonly ? (
          <ReadOnlyContent metadataArray={metadataArray} />
        ) : (
          <EditableContent
            metadataArray={metadataArray}
            jsonToggleByIndex={jsonToggleByIndex}
            handleInputChange={handleInputChange}
            toggleJsonEditor={toggleJsonEditor}
            deleteRow={deleteRow}
          />
        )}
      </div>

      {!readonly && (
        <div className="mt-2 flex justify-between">
          <Button
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={addNewRow}
          >
            <PlusIcon className="mr-1 h-3 w-3" />
            Add Field
          </Button>

          <Button
            variant="blueOutline"
            size="sm"
            className="text-xs"
            onClick={validateAndSave}
          >
            Update Metadata
          </Button>
        </div>
      )}
    </div>
  );
};

export default MetadataSection;
